import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import { ScallopMetricSummary } from '../ScallopTables/ScallopTables';
import './SamsFleetAvgF.css';

interface ScallopMetricRow {
  mean_F?: number;
}

interface SamsFleetAvgFData {
  scallopMetricSummary?: ScallopMetricRow[];
  baselineScallopMetricSummary?: ScallopMetricRow[];
  metricSummaryLoading?: boolean;
  metricSummaryError?: string | null;
  fleetAverageF?: number;
  onOpenScallopMetricsModal?: () => void;
}

function computeFleetAverageF(
  scallopMetricSummary: ScallopMetricRow[] | undefined
): number | null {
  if (!scallopMetricSummary || scallopMetricSummary.length === 0) {
    return null;
  }
  const sum = scallopMetricSummary.reduce(
    (acc, row) => acc + (row.mean_F ?? 0),
    0
  );
  return sum / scallopMetricSummary.length;
}

export default function SamsFleetAvgF(
  props: RegistryComponentProps
): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<SamsFleetAvgFData>(props);
  const [showModal, setShowModal] = useState(false);

  const pageScallopMetricSummary = pageData?.data?.scallopMetricSummary as ScallopMetricRow[] | undefined;
  const pageBaselineScallopMetricSummary = pageData?.data?.baselineScallopMetricSummary as ScallopMetricRow[] | undefined;
  const pageMetricSummaryLoading = pageData?.data?.metricSummaryLoading as boolean | undefined;
  const pageMetricSummaryError = pageData?.data?.metricSummaryError as string | null | undefined;
  const selectedModel = pageData?.data?.selectedModel as string | undefined;

  const scallopMetricSummary = data.scallopMetricSummary ?? pageScallopMetricSummary ?? [];
  const baselineScallopMetricSummary = data.baselineScallopMetricSummary ?? pageBaselineScallopMetricSummary ?? [];
  const metricSummaryLoading = data.metricSummaryLoading ?? pageMetricSummaryLoading ?? false;
  const metricSummaryError = data.metricSummaryError ?? pageMetricSummaryError ?? null;

  const fleetAverageF =
    data.fleetAverageF ?? computeFleetAverageF(scallopMetricSummary);
  const canOpenModal = false;
  const onOpenModal = canOpenModal ? () => setShowModal(true) : undefined;

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener('openScallopMetricsModal', handler);
    return () => window.removeEventListener('openScallopMetricsModal', handler);
  }, []);

  return (
    <div className="sams-fleet-avg-f insight-card">
      {/* <div className="insight-title-row">
        <h3>SAMS Assessment Metrics</h3>
      </div> */}
      <div className="insight-subtitle-container">
        <h2 className="insight-subtitle">
          Fleet-wide assessment metrics averaged across projection period
        </h2>
      </div>
      <div className="insight-columns" style={{ marginTop: '10px' }}>
        <div
          className={`insight-column ${onOpenModal ? 'clickable-column' : ''}`}
          style={{
            marginRight: 0,
            cursor: onOpenModal ? 'pointer' : 'default',
          }}
          onClick={onOpenModal ?? undefined}
        >
          <div className="assessment-metric-item">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span
                className="column-header"
                style={{ textAlign: 'left', marginBottom: 0 }}
              >
                Fleet Average F
              </span>
              <span className="insight-value" style={{ textAlign: 'right' }}>
                {fleetAverageF != null
                  ? `${fleetAverageF.toFixed(4)} yr⁻¹`
                  : '---'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {showModal &&
        createPortal(
          <Modal
            title="SAMS Projection Summary: Area Performance Metrics"
            onClose={handleCloseModal}
            size="extra-extra-large"
            transparentBackdrop={true}
            parameterStyle={true}
            {...({ customMaxWidth: '95vw', customMaxHeight: '90vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px', overflow: 'auto' }}
          >
            {metricSummaryLoading ? (
              <p className="noscroll-loading-message">Loading metrics...</p>
            ) : metricSummaryError ? (
              <p className="noscroll-error-message">{metricSummaryError}</p>
            ) : !scallopMetricSummary || scallopMetricSummary.length === 0 ? (
              <p className="noscroll-no-data-message">No metrics data available</p>
            ) : (
              <ScallopMetricSummary
                summaryData={scallopMetricSummary}
                baselineSummaryData={baselineScallopMetricSummary}
                loading={metricSummaryLoading}
                error={metricSummaryError}
              />
            )}
          </Modal>,
          document.body
        )}
    </div>
  );
}
